/**
 * Invoice Form
 *
 * Create and edit invoices with line items.
 *
 * Pattern: FormPanel (pannello laterale destro, mobile = full screen)
 */

import React, { useState, useEffect } from 'react';
import {
  FormField,
  FormRow,
  FormSection,
  FormActions,
  TextInput,
  TextArea,
  Select,
  DateInput,
  CurrencyInput,
  NumberInput,
} from './FormComponents';
import { FormPanel, FormPanelActions } from './FormPanel';
import * as customersApi from '../../api/customers';
import type { Customer } from '../../api/customers';
import type { Invoice, InvoiceItem, InvoiceStatus } from '@business-assistant/shared/data/types';
import type { InvoiceDirection } from '@business-assistant/shared/domain/models';

// =============================================================================
// TYPES
// =============================================================================

interface InvoiceFormData {
  number: string;
  issueDate: string;
  dueDate: string;
  direction: InvoiceDirection;
  customerId: string;
  customerName: string;
  items: InvoiceItem[];
  notes: string;
  status: InvoiceStatus;
  // Calculated totals
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
}

interface InvoiceFormProps {
  isOpen: boolean;
  invoice?: Invoice;
  onSave: (data: InvoiceFormData) => void;
  onCancel: () => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DIRECTION_OPTIONS = [
  { value: 'issued', label: 'Fattura emessa (vendita)' },
  { value: 'received', label: 'Fattura ricevuta (acquisto)' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Bozza' },
  { value: 'sent', label: 'Inviata' },
  { value: 'paid', label: 'Pagata' },
  { value: 'overdue', label: 'Scaduta' },
  { value: 'cancelled', label: 'Annullata' },
];

const VAT_RATES = [
  { value: '0.22', label: '22%' },
  { value: '0.10', label: '10%' },
  { value: '0.04', label: '4%' },
  { value: '0', label: 'Esente' },
];

// =============================================================================
// HELPERS
// =============================================================================

function generateInvoiceNumber(): string {
  const year = new Date().getFullYear();
  const num = Math.floor(Math.random() * 1000) + 1;
  return `${year}/${num.toString().padStart(4, '0')}`;
}

function calculateItemTotal(item: InvoiceItem): number {
  const net = item.quantity * item.unitPrice;
  return Math.round(net * (1 + item.vatRate));
}

// =============================================================================
// LINE ITEM ROW
// =============================================================================

interface LineItemRowProps {
  item: InvoiceItem;
  index: number;
  onChange: (index: number, item: InvoiceItem) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

function LineItemRow({ item, index, onChange, onRemove, canRemove }: LineItemRowProps) {
  const handleChange = (field: keyof InvoiceItem, value: string | number) => {
    const updated = { ...item, [field]: value };
    // Recalculate total
    updated.total = calculateItemTotal(updated);
    onChange(index, updated);
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 80px 120px 80px 120px 40px',
        gap: '0.5rem',
        alignItems: 'start',
        padding: '0.75rem',
        background: 'var(--bg-secondary, #f9fafb)',
        borderRadius: '0.5rem',
        marginBottom: '0.5rem',
      }}
    >
      <div>
        <TextInput
          value={item.description}
          onChange={(v) => handleChange('description', v)}
          placeholder="Descrizione"
        />
      </div>
      <div>
        <NumberInput
          value={item.quantity}
          onChange={(v) => handleChange('quantity', v)}
          min={1}
          step={1}
        />
      </div>
      <div>
        <CurrencyInput
          value={item.unitPrice}
          onChange={(v) => handleChange('unitPrice', v)}
        />
      </div>
      <div>
        <Select
          value={item.vatRate.toString()}
          onChange={(v) => handleChange('vatRate', parseFloat(v))}
          options={VAT_RATES}
        />
      </div>
      <div
        style={{
          padding: '0.625rem 0.75rem',
          background: 'var(--bg, white)',
          borderRadius: '0.5rem',
          fontWeight: 600,
          textAlign: 'right',
        }}
      >
        {formatCurrency(item.total)}
      </div>
      <div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          style={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: canRemove ? '#fee2e2' : 'transparent',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: canRemove ? 'pointer' : 'not-allowed',
            opacity: canRemove ? 1 : 0.3,
          }}
        >
          <svg width="16" height="16" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function InvoiceForm({ isOpen, invoice, onSave, onCancel }: InvoiceFormProps) {
  const isEditing = !!invoice;
  const isNonDraft = isEditing && invoice?.status !== 'draft';

  // Show confirmation for non-draft invoices
  const [hasConfirmedEdit, setHasConfirmedEdit] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Check if editing non-draft invoice and show confirmation
  useEffect(() => {
    if (isOpen && isNonDraft && !hasConfirmedEdit) {
      setShowConfirmDialog(true);
    }
  }, [isOpen, isNonDraft, hasConfirmedEdit]);

  const handleConfirmEdit = () => {
    setHasConfirmedEdit(true);
    setShowConfirmDialog(false);
  };

  const handleCancelEdit = () => {
    setShowConfirmDialog(false);
    onCancel();
  };

  const [formData, setFormData] = useState<InvoiceFormData>({
    number: invoice?.number || generateInvoiceNumber(),
    issueDate: invoice?.issueDate || invoice?.date || new Date().toISOString().split('T')[0],
    dueDate: invoice?.dueDate || '',
    direction: invoice?.direction || 'issued',
    customerId: invoice?.customerId || '',
    customerName: invoice?.customerName || '',
    items: invoice?.items || [
      { description: '', quantity: 1, unitPrice: 0, vatRate: 0.22, total: 0 },
    ],
    notes: invoice?.notes || '',
    status: invoice?.status || 'draft',
    netTotal: 0,
    vatTotal: 0,
    grossTotal: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load customers from API
  const [customersList, setCustomersList] = useState<Customer[]>([]);
  useEffect(() => {
    const loadCustomers = async () => {
      const result = await customersApi.listCustomers();
      if (result.success && result.data) {
        setCustomersList(result.data);
      }
    };
    loadCustomers();
  }, []);

  // Customer options for dropdown
  const customerOptions = customersList.map((c) => ({
    value: c.id,
    label: c.type === 'company' ? c.companyName! : `${c.firstName} ${c.lastName}`,
  }));

  // Calculate totals
  const netTotal = formData.items.reduce((sum, item) => {
    return sum + item.quantity * item.unitPrice;
  }, 0);

  const vatTotal = formData.items.reduce((sum, item) => {
    return sum + item.quantity * item.unitPrice * item.vatRate;
  }, 0);

  const grossTotal = netTotal + vatTotal;

  // Update formData totals when items change
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      netTotal,
      vatTotal,
      grossTotal,
    }));
  }, [netTotal, vatTotal, grossTotal]);

  // Handlers
  const updateField = <K extends keyof InvoiceFormData>(
    field: K,
    value: InvoiceFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Handle customer selection - also set customerName
  const handleCustomerChange = (customerId: string) => {
    const customer = customersList.find((c) => c.id === customerId);
    const customerName = customer
      ? customer.type === 'company'
        ? customer.companyName || ''
        : `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
      : '';
    setFormData((prev) => ({
      ...prev,
      customerId,
      customerName,
    }));
    if (errors.customerId) {
      setErrors((prev) => ({ ...prev, customerId: '' }));
    }
  };

  const updateItem = (index: number, item: InvoiceItem) => {
    const newItems = [...formData.items];
    newItems[index] = item;
    updateField('items', newItems);
  };

  const addItem = () => {
    updateField('items', [
      ...formData.items,
      { description: '', quantity: 1, unitPrice: 0, vatRate: 0.22, total: 0 },
    ]);
  };

  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      updateField(
        'items',
        formData.items.filter((_, i) => i !== index)
      );
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.number.trim()) {
      newErrors.number = 'Numero fattura obbligatorio';
    }
    if (!formData.issueDate) {
      newErrors.issueDate = 'Data emissione obbligatoria';
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Cliente obbligatorio';
    }
    if (formData.items.length === 0 || formData.items.every((i) => !i.description.trim())) {
      newErrors.items = 'Aggiungi almeno una voce';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  // Invoice icon
  const invoiceIcon = (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );

  // Status labels for confirmation dialog
  const statusLabels: Record<string, string> = {
    sent: 'inviata',
    paid: 'pagata',
    overdue: 'scaduta',
    cancelled: 'annullata',
  };

  // Confirmation dialog for non-draft invoices
  if (showConfirmDialog) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="20" height="20" fill="none" stroke="#f59e0b" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
              Modifica fattura {statusLabels[invoice?.status || ''] || 'non in bozza'}
            </h3>
          </div>
          <p style={{ margin: '0 0 20px', color: '#6b7280', lineHeight: 1.5 }}>
            Questa fattura e gia stata <strong>{statusLabels[invoice?.status || ''] || 'processata'}</strong>.
            Modificarla potrebbe creare incongruenze con i documenti fiscali.
            <br /><br />
            Sei sicuro di voler procedere con la modifica?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleCancelEdit}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Annulla
            </button>
            <button
              onClick={handleConfirmEdit}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                background: '#f59e0b',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Procedi con la modifica
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormPanel
      isOpen={isOpen}
      onClose={onCancel}
      title={isEditing ? 'Modifica Fattura' : 'Nuova Fattura'}
      subtitle={isEditing ? `Fattura n. ${formData.number}` : 'Crea una nuova fattura'}
      icon={invoiceIcon}
      headerColor="#3b82f6"
      width={700}
      footer={
        <FormPanelActions>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--line, #ddd)',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              background: '#3b82f6',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {isEditing ? 'Salva Modifiche' : 'Crea Fattura'}
          </button>
        </FormPanelActions>
      }
    >
          <FormSection title="Dati Fattura">
            <FormRow columns={3}>
              <FormField label="Numero" required error={errors.number}>
                <TextInput
                  value={formData.number}
                  onChange={(v) => updateField('number', v)}
                  placeholder="2024/0001"
                />
              </FormField>
              <FormField label="Data emissione" required error={errors.issueDate}>
                <DateInput
                  value={formData.issueDate}
                  onChange={(v) => updateField('issueDate', v)}
                />
              </FormField>
              <FormField label="Scadenza">
                <DateInput
                  value={formData.dueDate}
                  onChange={(v) => updateField('dueDate', v)}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField label="Tipo" required>
                <Select
                  value={formData.direction}
                  onChange={(v) => updateField('direction', v as InvoiceDirection)}
                  options={DIRECTION_OPTIONS}
                />
              </FormField>
              <FormField label="Stato">
                <Select
                  value={formData.status}
                  onChange={(v) => updateField('status', v as InvoiceStatus)}
                  options={STATUS_OPTIONS}
                />
              </FormField>
            </FormRow>

            <FormField label="Cliente" required error={errors.customerId}>
              <Select
                value={formData.customerId}
                onChange={handleCustomerChange}
                options={customerOptions}
                placeholder="Seleziona cliente..."
              />
            </FormField>
          </FormSection>

          <FormSection title="Voci Fattura">
            {/* Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 120px 80px 120px 40px',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'var(--text-muted, #6b7280)',
                textTransform: 'uppercase',
              }}
            >
              <span>Descrizione</span>
              <span>Qtà</span>
              <span>Prezzo</span>
              <span>IVA</span>
              <span style={{ textAlign: 'right' }}>Totale</span>
              <span></span>
            </div>

            {/* Items */}
            {formData.items.map((item, index) => (
              <LineItemRow
                key={index}
                item={item}
                index={index}
                onChange={updateItem}
                onRemove={removeItem}
                canRemove={formData.items.length > 1}
              />
            ))}

            {errors.items && (
              <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                {errors.items}
              </div>
            )}

            <button
              type="button"
              onClick={addItem}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 0.75rem',
                background: 'transparent',
                border: '1px dashed var(--border, #e5e7eb)',
                borderRadius: '0.5rem',
                fontSize: '0.8125rem',
                color: 'var(--brand, #3b82f6)',
                cursor: 'pointer',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Aggiungi voce
            </button>

            {/* Totals */}
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'var(--bg-secondary, #f9fafb)',
                borderRadius: '0.5rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: 'var(--text-muted, #6b7280)' }}>Imponibile</span>
                <span>{formatCurrency(netTotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: 'var(--text-muted, #6b7280)' }}>IVA</span>
                <span>{formatCurrency(vatTotal)}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid var(--border, #e5e7eb)',
                  fontWeight: 600,
                  fontSize: '1.125rem',
                }}
              >
                <span>Totale</span>
                <span style={{ color: 'var(--brand, #3b82f6)' }}>{formatCurrency(grossTotal)}</span>
              </div>
            </div>
          </FormSection>

      <FormSection title="Note">
        <TextArea
          value={formData.notes}
          onChange={(v) => updateField('notes', v)}
          placeholder="Note aggiuntive..."
          rows={3}
        />
      </FormSection>
    </FormPanel>
  );
}

export type { InvoiceFormData };
